import React, { useState, useEffect } from "react";
import crossPink from "../../../images/crossPink.png";
import fileIcon from "../../../images/redAlert.png";
import gridIcon from "../../../images/grid.png";
import graphIcon from "../../../images/tree.png";
import graphIconclicked from "../../../images/treeonclick.png";
import gridIconclicked from "../../../images/gridonclick.png";
import styles from "./HistoryLog.module.scss";
import CheckIcon from "@mui/icons-material/Check";
import ArrowUpwardIcon from "@mui/icons-material/KeyboardArrowUp";
import { format } from "date-fns";
import ContractHeader from "../../../Components/ContractHeader";
import { Dialog, Classes, Icon, Intent } from "@blueprintjs/core";
import { GET } from "../../dataSaver";
import yellowAlert from "../../../images/yellowAlert.png";
import greenCheck from "../../../images/checkImage.png";
import Table from "../../../Components/TableDesign";

const isBlankFieldValue = (val) => !val || val === 'string';

// Groups a change's fields by the specific record they belong to (e.g. a service or
// timesheet term), so a save touching multiple records is shown per-record instead of
// as one undifferentiated list of field names.
const groupFieldsByEntity = (fields = []) => {
  const groups = [];
  const indexByEntity = new Map();

  fields.forEach((field) => {
    const label = field.fieldLabel || field.fieldName;
    if (isBlankFieldValue(label)) return;

    const entityKey = isBlankFieldValue(field.entityName) ? '' : field.entityName;
    if (!indexByEntity.has(entityKey)) {
      indexByEntity.set(entityKey, groups.length);
      groups.push({ entityName: entityKey, labels: [] });
    }
    groups[indexByEntity.get(entityKey)].labels.push(label);
  });

  return groups;
};

const formatFieldChangeSummary = (fields = []) => {
  return groupFieldsByEntity(fields)
    .map(({ entityName, labels }) => {
      const labelText = labels.join(', ');
      return entityName ? `${labelText} for "${entityName}"` : labelText;
    })
    .join('; ');
};

const HistoryLog = ({ isOpen = true, onClose, contractId, contractData }) => {
  const [isTableView, setIsTableView] = useState(false);
  const [apiContractData, setApiContractData] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiMessage, setApiMessage] = useState(null);
  const [sitesData, setSitesData] = useState([]);
  const [siteControlledAtSiteLevel, setSiteControlledAtSiteLevel] = useState(false);

  const fetchWorkflowData = async (apiContractId) => {
    try {
      console.log('Fetching workflow data for contractId:', apiContractId);
      const response = await GET(`timesheet-management-service/contractWorkflow/${apiContractId}`);
      console.log('Workflow API response:', response.data);
      setWorkflowData(response.data);
    } catch (error) {
      console.error('Error fetching workflow data:', error);
      setWorkflowData(null);
    }
  };

  const checkSiteControlLevel = (contractData, sites) => {
    try {
      const primarySite = contractData?.contract?.contractDetail?.site?.sites?.find(site => site.primarySite === true);
      const contractSiteIds = primarySite ? [primarySite.id] : [];
      const siteControlledSites = (sites || []).filter(site =>
        contractSiteIds.includes(site.id) &&
        site.invoiceWorkflowControlLevelBy !== 'CONTRACT'
      );
      return siteControlledSites.length > 0;
    } catch (error) {
      console.error('Error checking site control level:', error);
      return false;
    }
  };

  const fetchContractData = async () => {
    if (!contractId) {
      console.log("No contractId provided, skipping API call");
      return;
    }

    try {
      const apiUrl = `contract-managment-service/contracts/${contractId}`;
      console.log("Fetching contract data with API URL:", apiUrl);

      const response = await GET(apiUrl);
      console.log("Contract API response:", response);

      if (response.data) {
        setApiContractData(response.data);
        console.log("Contract data fetched successfully:", response.data);
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
      throw error; // Re-throw to be caught by the Promise.all
    }
  };

  const fetchAuditLogs = async () => {
    if (!contractId) {
      console.log("No contractId");
      return;
    }

    try {
      const auditLogsUrl = `contract-managment-service/contracts/${contractId}/auditLogs`;
      console.log("Fetching audit logs with API URL:", auditLogsUrl);

      const auditResponse = await GET(auditLogsUrl);
      console.log("Audit logs API response:", auditResponse);

      // Handle different API response scenarios
      if (auditResponse.data && Array.isArray(auditResponse.data) && auditResponse.data.length > 0) {
        setAuditLogs(auditResponse.data);
        setApiMessage(null);
        console.log("Audit logs fetched successfully:", auditResponse.data);
        console.log("Number of audit logs:", auditResponse.data.length);

        // Debug invalid logs
        const invalidLogs = auditResponse.data.filter(log =>
          !log || !log.createdAt || !log.action || log.action === 'undefined'
        );
        if (invalidLogs.length > 0) {
          console.warn("Found invalid audit logs:", invalidLogs);
        }
      } else {
        setAuditLogs([]);
        if (auditResponse.message) {
          setApiMessage(auditResponse.message);
          console.log("API message:", auditResponse.message);
        } else if (typeof auditResponse.data === 'string') {
          setApiMessage(auditResponse.data);
          console.log("API data as message:", auditResponse.data);
        } else {
          console.log("No audit logs data in response, setting default message");
        }
      }
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      if (error.response?.data?.message) {
        setApiMessage(error.response.data.message);
      } else if (error.message) {
        setApiMessage(error.message);
      } else {
        setApiMessage(`Error fetching audit logs for contract ID: ${contractId}`);
      }

      setAuditLogs([]);
      throw error; // Re-throw to be caught by the Promise.all
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        setLoading(true);
        setError(null);
        setApiMessage(null);

        try {
          const promises = [];


          if (contractId) {
            promises.push(fetchAuditLogs());
            promises.push(fetchContractData());
            promises.push(fetchWorkflowData(contractId));
          } else {
            console.log("No contractId provided, cannot fetch data");
            setError("No contract ID provided");
            return;
          }

          await Promise.all(promises);

          // Fetch sites and contract data in parallel to determine if invoice is site-controlled
          try {
            const [sitesResponse, contractResponse] = await Promise.all([
              GET('entity-service/sites'),
              GET(`contract-managment-service/contracts/${contractId}`)
            ]);
            const sites = sitesResponse?.data || [];
            setSitesData(sites);
            const isSiteControlled = checkSiteControlLevel(contractResponse?.data, sites);
            setSiteControlledAtSiteLevel(isSiteControlled);
          } catch (sitesError) {
            console.error('Error fetching sites data:', sitesError);
          }

          if (apiContractData && (apiContractData.timesheetWorkflows || apiContractData.invoiceWorkflows)) {
            setWorkflowData({
              timesheetWorkflows: apiContractData.timesheetWorkflows || [],
              invoiceWorkflows: apiContractData.invoiceWorkflows || []
            });
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          setError("Failed to fetch data. Please try again.");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [isOpen, contractId]);

  const getWorkflowSectionStatus = (sectionTitle, workflowData) => {
    if (!workflowData) {
      return "inactive";
    }

    switch (sectionTitle) {
      case "Activity Logging":
        if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
          const hasIncompleteActivityLogging = workflowData.timesheetWorkflows.some(workflow => {
            const activityLogDetail = workflow.activityLoggerDetail;
            return !activityLogDetail || activityLogDetail.completionStatus !== "COMPLETED";
          });

          const hasPartialActivityLogging = workflowData.timesheetWorkflows.some(workflow => {
            const activityLogDetail = workflow.activityLoggerDetail;
            return activityLogDetail && activityLogDetail.completionStatus === "PARTIAL";
          });

          if (hasIncompleteActivityLogging) {
            return hasPartialActivityLogging ? "partial" : "not_completed";
          }
          return "completed";
        }
        return "inactive";

      case "Timesheet Creation & Submission":
        if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
          const hasSubmissionUsers = workflowData.timesheetWorkflows.some(workflow =>
            workflow.timesheetSubmissionUser !== null
          );

          if (!hasSubmissionUsers) {
            return "not_completed";
          }

          const hasIncompleteSubmission = workflowData.timesheetWorkflows.some(workflow => {
            const submissionUser = workflow.timesheetSubmissionUser;
            return submissionUser && submissionUser.completionStatus !== "COMPLETED";
          });

          const hasPartialSubmission = workflowData.timesheetWorkflows.some(workflow => {
            const submissionUser = workflow.timesheetSubmissionUser;
            return submissionUser && submissionUser.completionStatus === "PARTIAL";
          });

          if (hasIncompleteSubmission) {
            return hasPartialSubmission ? "partial" : "not_completed";
          }
          return "completed";
        }
        return "inactive";

      case "Timesheet Review & Approval":
        if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
          const hasWorkflowConfig = workflowData.timesheetWorkflows.some(workflow =>
            workflow.timesheetWorkflowConfiguration !== null
          );

          if (!hasWorkflowConfig) {
            return "not_completed";
          }

          const hasIncompleteWorkflow = workflowData.timesheetWorkflows.some(workflow => {
            const config = workflow.timesheetWorkflowConfiguration;
            if (!config) return true;

            if (config.completionStatus === "NOT_COMPLETED") return true;
            if (!config.hasTimesheetApproval) return true;
            // Check authorizedUserIds array instead of noAuthorizedUsers count
            if (!config.authorizedUserIds || config.authorizedUserIds.length === 0) return true;

            return false;
          });

          const hasPartialWorkflow = workflowData.timesheetWorkflows.some(workflow => {
            const config = workflow.timesheetWorkflowConfiguration;
            return config && config.completionStatus === "PARTIAL";
          });

          if (hasIncompleteWorkflow) {
            return hasPartialWorkflow ? "partial" : "not_completed";
          }
          return "completed";
        }
        return "inactive";

      case "Invoice Review & Approval":
        if (workflowData.invoiceWorkflows && workflowData.invoiceWorkflows.length > 0) {
          // If invoiceWorkflowControlLevelBy is CONTRACT and all invoices are NOT_COMPLETED,
          // treat as inactive — not yet configured, don't block workflow completion
          if (workflowData.invoiceWorkflowControlLevelBy === 'CONTRACT') {
            const allNotCompleted = workflowData.invoiceWorkflows.every(invoiceWorkflow =>
              !invoiceWorkflow.completionStatus || invoiceWorkflow.completionStatus === 'NOT_COMPLETED'
            );
            if (allNotCompleted) {
              return "inactive";
            }
          }

          const hasIncompleteInvoice = workflowData.invoiceWorkflows.some(invoiceWorkflow => {
            if (invoiceWorkflow.completionStatus === "NOT_COMPLETED") return true;
            if (!invoiceWorkflow.controlLevels) return true;
            if (!invoiceWorkflow.authorizedUserIds || invoiceWorkflow.authorizedUserIds.length === 0) return true;

            return false;
          });

          const hasPartialInvoice = workflowData.invoiceWorkflows.some(invoiceWorkflow => {
            return invoiceWorkflow.completionStatus === "PARTIAL";
          });

          if (hasIncompleteInvoice) {
            return hasPartialInvoice ? "partial" : "not_completed";
          }
          return "completed";
        }
        return "inactive";

      default:
        return "inactive";
    }
  };

  const getAlertsAndIcons = () => {
    const alerts = [];
    const alertIcons = [];

    if (!apiContractData?.contract) {
      alerts.push("Contract Data Required");
      alerts.push("Workflow Configuration Incomplete");
      alertIcons.push(fileIcon);
      alertIcons.push(yellowAlert);
      return { alerts, alertIcons };
    }

    const contract = apiContractData.contract;
    let hasDocuments = false;
    if (contract.contractDetail?.contractFiles && contract.contractDetail.contractFiles.length > 0) {
      const validDocuments = contract.contractDetail.contractFiles.filter(file =>
        file && file.fileURL && file.fileURL.trim() !== ''
      );
      hasDocuments = validDocuments.length > 0;
    }

    const contractSections = [
      {
        title: "Attached Contract Document(s)",
        status: hasDocuments ? "completed" : "not_completed"
      },
      {
        title: "Contract Identification & Term Limit",
        status: (() => {
          if (contract.contractDetail) {
            return "completed";
          }
          if (!contract.contractDetail) {
            return "inactive";
          }
          return "inactive";
        })()
      },
      {
        title: "Contracted Services Provider(s)",
        status: (() => {
          if (!contract.contractServiceProviders || contract.contractServiceProviders.length === 0) {
            return "completed";
          }

          const hasIncompleteProviders = contract.contractServiceProviders.some(provider => {
            const hasBasicInfo = provider.name?.firstName &&
              provider.name?.lastName &&
              provider.contractedServiceProviderType?.id &&
              provider.sites?.sites?.length > 0;

            return !hasBasicInfo;
          });

          if (hasIncompleteProviders) {
            return "not_completed";
          } else {
            return "completed";
          }
        })()
      },
      {
        title: "Contractor Business Entity",
        status: (() => {
          if (!contract.contractorBusinessEntity || contract.contractorBusinessEntity.id === null) {
            return "inactive";
          }

          if (contract.contractorBusinessEntity.completionStatus === "NOT_COMPLETED") {
            return "not_completed";
          }
          if (contract.contractorBusinessEntity.completionStatus === "PARTIAL") {
            return "partial";
          }
          if (contract.contractorBusinessEntity.completionStatus === "COMPLETED") {
            return "completed";
          }

          return "inactive";
        })()
      },
      {
        title: "Contracted Services Specification",
        status: (() => {
          if (!contract.contractedServices || contract.contractedServices.length === 0) {
            return "inactive";
          }

          const hasIncompleteServices = contract.contractedServices.some(service =>
            service.completionStatus !== "COMPLETED"
          );

          if (hasIncompleteServices) {
            return "not_completed";
          } else {
            return "completed";
          }
        })()
      },
      {
        title: "Timesheet Submission Terms",
        status: (() => {
          const timesheetTermsArray = Array.isArray(contract.timesheetSubmissionTerms) ?
            contract.timesheetSubmissionTerms :
            (contract.timesheetSubmissionTerms ? [contract.timesheetSubmissionTerms] : []);

          if (timesheetTermsArray.length === 0) {
            return "inactive";
          }

          const hasNotCompletedTerms = timesheetTermsArray.some(terms =>
            terms.completionStatus === "NOT_COMPLETED"
          );
          const hasPartialTerms = timesheetTermsArray.some(terms =>
            terms.completionStatus === "PARTIAL"
          );
          const hasCompletedTerms = timesheetTermsArray.some(terms =>
            terms.completionStatus === "COMPLETED"
          );

          if (hasNotCompletedTerms) {
            return "not_completed";
          }
          if (hasPartialTerms) {
            return "partial";
          }
          if (hasCompletedTerms) {
            return "completed";
          }

          return "inactive";
        })()
      },
      {
        title: "Payment & Compensation",
        status: (() => {
          if (!contract.paymentAndCompensation) {
            return "inactive";
          }
          if (contract.paymentAndCompensation.completionStatus === "NOT_COMPLETED") {
            return "not_completed";
          }
          if (contract.paymentAndCompensation.completionStatus === "PARTIAL") {
            return "partial";
          }
          if (contract.paymentAndCompensation.completionStatus === "COMPLETED") {
            return "completed";
          }

          return "inactive";
        })()
      }
    ];

    const workflowSections = [
      {
        title: "Activity Logging",
        status: getWorkflowSectionStatus("Activity Logging", workflowData)
      },
      {
        title: "Timesheet Creation & Submission",
        status: getWorkflowSectionStatus("Timesheet Creation & Submission", workflowData)
      },
      {
        title: "Timesheet Review & Approval",
        status: getWorkflowSectionStatus("Timesheet Review & Approval", workflowData)
      },
      // Only include Invoice if not controlled at site level
      ...(!siteControlledAtSiteLevel ? [{
        title: "Invoice Review & Approval",
        status: getWorkflowSectionStatus("Invoice Review & Approval", workflowData)
      }] : [])
    ];

    const sectionsWithWarnings = contractSections.filter(section => section.status === 'not_completed');
    const sectionsWithPartials = contractSections.filter(section => section.status === 'partial');
    const allContractSectionsCompleted = contractSections.every(section => section.status === 'completed');

    if (sectionsWithWarnings.length > 0) {
      alerts.push('Contract Data Required');
      alertIcons.push(fileIcon);
    } else if (sectionsWithPartials.length > 0) {
      alerts.push('Contract Data Partially Completed');
      alertIcons.push(yellowAlert);
    } else if (allContractSectionsCompleted) {
      alerts.push('Contract Data Completed');
      alertIcons.push(greenCheck);
    } else {
      alerts.push('Contract Data Required');
      alertIcons.push(fileIcon);
    }

    const incompleteWorkflows = workflowSections.filter(section =>
      section.status === 'not_completed'
    );
    const partialWorkflows = workflowSections.filter(section => section.status === 'partial');
    const activeSections = workflowSections.filter(section => section.status !== 'inactive');
    const allWorkflowsCompleted = activeSections.length > 0 &&
      activeSections.every(section => section.status === 'completed');

    if (incompleteWorkflows.length > 0) {
      alerts.push('Workflow Configuration Incomplete');
      alertIcons.push(fileIcon);
    } else if (partialWorkflows.length > 0) {
      alerts.push('Workflow Configuration Partially Completed');
      alertIcons.push(yellowAlert);
    } else if (allWorkflowsCompleted) {
      alerts.push('Workflow Configuration Completed');
      alertIcons.push(greenCheck);
    } else {
      alerts.push('Workflow Configuration Incomplete');
      alertIcons.push(fileIcon);
    }

    return { alerts, alertIcons };
  };

  const getAlertIcon = () => {
    if (!apiContractData?.contract) {
      return fileIcon;
    }

    const contract = apiContractData.contract;

    const contractSections = [];

    const workflowSections = [
      {
        status: getWorkflowSectionStatus("Activity Logging", workflowData)
      },
      {
        status: getWorkflowSectionStatus("Timesheet Creation & Submission", workflowData)
      },
      {
        status: getWorkflowSectionStatus("Timesheet Review & Approval", workflowData)
      },
      {
        status: getWorkflowSectionStatus("Invoice Review & Approval", workflowData)
      }
    ];

    const { alerts } = getAlertsAndIcons();
    const contractDataComplete = alerts.includes('Contract Data Completed');
    const workflowDataComplete = alerts.includes('Workflow Configuration Completed');
    const hasContractWarnings = alerts.includes('Contract Data Required');
    const hasWorkflowIncomplete = alerts.includes('Workflow Configuration Incomplete');
    const hasWorkflowPartial = alerts.includes('Workflow Configuration Partially Completed');
    const hasPartials = alerts.includes('Contract Data Partially Completed');

    if (hasContractWarnings && !contractDataComplete) {
      return fileIcon;
    }

    if ((contractDataComplete && !workflowDataComplete) ||
      (!contractDataComplete && workflowDataComplete) ||
      hasPartials ||
      hasWorkflowIncomplete ||
      hasWorkflowPartial) {
      return yellowAlert;
    }

    if (contractDataComplete && workflowDataComplete) {
      return greenCheck;
    }

    return fileIcon;
  };

  const { alerts, alertIcons } = getAlertsAndIcons();

  const transformAuditLogsToHistoryData = () => {
    if (!auditLogs || auditLogs.length === 0) {
      return [];
    }
    const sortedLogs = [...auditLogs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Helper function to get user name from a log entry
    const getUserNameFromLog = (log) => {
      if (log.changedBy?.name?.name) return log.changedBy.name.name;
      if (log.changedBy?.name?.firstName && log.changedBy?.name?.lastName) {
        return `${log.changedBy.name.firstName} ${log.changedBy.name.lastName}`;
      }
      return null;
    };

    // Helper function to find user name from nearby entries when current entry has "Unknown User"
    const findUserNameFromNearbyEntries = (currentLog, allLogs) => {
      const currentTime = new Date(currentLog.createdAt).getTime();

      // Look for entries within 5 minutes that have valid user info
      for (const otherLog of allLogs) {
        if (otherLog === currentLog) continue;

        const otherTime = new Date(otherLog.createdAt).getTime();
        const timeDiff = Math.abs(currentTime - otherTime);

        // Within 5 minutes
        if (timeDiff < 300000) {
          const userName = getUserNameFromLog(otherLog);
          if (userName) return userName;
        }
      }
      return '-';
    };

    return sortedLogs.map((log, index) => {
      const date = new Date(log.createdAt);
      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });

      let activity = '';
      let status = 'completed';
      if (log.action === 'CREATE') {
        if (log.changes && log.changes.length > 0) {
          const change = log.changes[0];
          const createdEntityNames = [...new Set((change.fields || [])
            .map(field => field.entityName)
            .filter(name => !isBlankFieldValue(name)))];
          const createdEntitySuffix = createdEntityNames.length > 0 ? ` "${createdEntityNames.join('", "')}"` : '';
          if (change.tabIdentifier === 'Contracted Services Provider(s)') {
            activity = 'Contracted Service Provider Created';
          } else if (!change.tabIdentifier || change.tabIdentifier === 'string') {
            activity = 'Created Record';
          } else {
            activity = `Created Record${createdEntitySuffix} in ${change.tabIdentifier}`;
          }
        } else {
          activity = 'Created Record by eCATS Processing';
        }
      } else if (log.action === 'UPDATE') {
        if (log.changes && log.changes.length > 0) {
          const change = log.changes[0];
          const fieldsUpdated = formatFieldChangeSummary(change.fields);
          const tabLabel = (!change.tabIdentifier || change.tabIdentifier === 'string') ? '' : ` in ${change.tabIdentifier}`;
          if (fieldsUpdated) {
            activity = `Updated ${fieldsUpdated}${tabLabel}`;
          } else {
            activity = `Record Updated${tabLabel}`;
          }
        } else {
          activity = 'Record Updated';
        }
      } else if (log.action === 'DELETE') {
        if (log.changes && log.changes.length > 0) {
          const change = log.changes[0];
          const deletedService = change.fields?.find(field => field.fieldName === 'deletedService');
          if (deletedService) {
            activity = `Deleted Service: ${deletedService.oldValue}`;
          } else {
            const deleteTabLabel = (!change.tabIdentifier || change.tabIdentifier === 'string') ? '' : ` from ${change.tabIdentifier}`;
            activity = `Deleted Record${deleteTabLabel}`;
          }
        } else {
          activity = 'Record Deleted';
        }
      } else {
        activity = `${log.action} Action Performed`;
      }

      if (index === 0) {
        status = 'highlighted';
      }

      // Get user name - if current entry has no user info, try to find from nearby entries
      let updatedByUser = getUserNameFromLog(log);
      if (!updatedByUser) {
        updatedByUser = findUserNameFromNearbyEntries(log, sortedLogs);
      }

      return {
        activity,
        date: formattedDate,
        updatedBy: updatedByUser,
        status,
        changes: log.changes || [],
        tabIdentifier: log.changes?.[0]?.tabIdentifier || '',
        fieldsChanged: log.changes?.[0]?.fields || [],
        rawLog: log
      };
    });
  };

  const historyData = transformAuditLogsToHistoryData();

  const getLeftTitle = (activity, index) => {
    if (activity.includes("Created Record")) return "Submitted On";
    if (activity.includes("Resent Requested")) return "Resent Requested On";
    if (activity.includes("Document Uploaded")) return "Uploaded On";
    if (activity.includes("Record Updated From Solo Provider"))
      return "Record Updated On";
    if (activity.includes("Record Updated")) return "Record Updated On";
    return "Record Updated On";
  };

  const isPlaceholder = (val) => !val || val === 'string';

  const getDetailedChangeInfo = (item, isTableView = false) => {
    if (isTableView) {
      return item.updatedBy;
    }

    if (!item.fieldsChanged || item.fieldsChanged.length === 0) {
      return item.updatedBy;
    }

    const fieldChanges = formatFieldChangeSummary(item.fieldsChanged);

    if (!fieldChanges) {
      return item.updatedBy;
    }

    const actionType = item.rawLog?.action === 'CREATE' ? 'Created' : 'Updated';
    const tab = !isPlaceholder(item.tabIdentifier) ? ` in ${item.tabIdentifier}` : '';

    return `${item.updatedBy} (${actionType}: ${fieldChanges}${tab})`;
  };

  const getRightTitle = (activity, index) => {
    if (activity.includes("Created Record")) return "Created By";
    if (activity.includes("Resent Requested")) return "Resent Requested By";
    if (activity.includes("Document Uploaded")) return "Document Uploaded By";
    if (activity.includes("Record Updated From Solo Provider"))
      return "Record Updated From Solo Provider Contract To Multi Provider Contract By";
    if (activity.includes("Record Updated")) return "Record Updated By";
    return "Record Updated By";
  };

  const tableHeaderValues = ["Activity Title", "Date & Time", "Updated By"];

  const tableDataValues = [
    {
      type: "text",
      value: historyData.map((item) => item.activity),
    },
    {
      type: "text",
      value: historyData.map((item) => item.date),
    },
    {
      type: "text",
      value: historyData.map((item) => getDetailedChangeInfo(item, true)),
    },
  ];

  if (!isOpen) return null;

  return (
    <Dialog
      isOpen={isOpen}
      className={`${styles.dialogStyle} ${styles.dialogPaddingBottom}`}
    >
      <div
        className={`${Classes.DIALOG_BODY} ${styles.extensionDialogBackground}`}
      >
        <div className={styles.spaceBetween}>
          <p className={styles.extensionStyle}>View History Log</p>
          <Icon
            icon="cross"
            size={20}
            intent={Intent.DANGER}
            className={styles.crossStyle}
            onClick={onClose}
          />
        </div>
        <div className={styles.extensionBorder}></div>
        <div style={{ paddingTop: "10px" }}>
          <ContractHeader
            contractTitle={
              apiContractData?.contract?.contractName?.contractName ||
              contractData?.contractName?.contractName ||
              contractData?.contractName ||
              "Contract Name"
            }
            isDraft={
              apiContractData?.contract?.contractStatus === "DRAFT" ||
              contractData?.contractStatus === "DRAFT"
            }
            contractSubtitle={
              apiContractData?.contract?.contractType
                ? apiContractData.contract.contractType === "MULTIPLE"
                  ? "Multi Provider Contract"
                  : apiContractData.contract.contractType === "BATCH"
                    ? "Batch Provider Contract"
                    : "Solo Provider Contract"
                : contractData?.contractType
                  ? contractData.contractType === "MULTIPLE"
                    ? "Multi Provider Contract"
                    : contractData.contractType === "BATCH"
                      ? "Batch Provider Contract"
                      : "Solo Provider Contract"
                  : "Contract Type"
            }
            contractId={
              apiContractData?.contract?.contractDetail?.contractId?.id ||
              contractData?.contractDetail?.contractId?.id ||
              contractData?.contractId ||
              contractData?.id ||
              contractId ||
              "Contract ID"
            }
            alerts={alerts}
            alertIcons={alertIcons}
            alertIcon={getAlertIcon()}
            lastUpdated={
              apiContractData?.contract?.lastModifiedDate
                ? format(new Date(apiContractData.contract.lastModifiedDate), "MMM dd, yyyy")
                : contractData?.lastModifiedDate
                  ? format(new Date(contractData.lastModifiedDate), "MMM dd, yyyy")
                  : "N/A"
            }
            lastUpdatedBy={workflowData?.lastModifiedBy?.name?.name}
          />

          <div className={styles.contentArea}>
            <div className={styles.viewToggleContainer}>
              <button
                className={styles.viewToggleButton}
                style={{ border: "none", outline: "none", boxShadow: "none" }}
                onClick={() => setIsTableView(false)}
              >
                <img
                  src={isTableView ? graphIcon : graphIconclicked}
                  alt="Graph"
                  className={styles.closeIcon}
                />
              </button>
              <button
                className={styles.viewToggleButton}
                style={{ border: "none", outline: "none", boxShadow: "none" }}
                onClick={() => setIsTableView(true)}
              >
                <img src={isTableView ? gridIconclicked : gridIcon} alt="Grid" className={styles.gridIcon} />
              </button>
            </div>
            {loading ? (
              <div className={styles.loadingContainer}>
                <p>Loading history data...</p>
              </div>
            ) : error ? (
              <div className={styles.errorContainer}>
                <p>Error loading history data</p>
                <p>{error}</p>
              </div>
            ) : historyData.length === 0 ? (
              <div className={styles.emptyContainer}>
                <p>No history records found</p>
              </div>
            ) : isTableView ? (
              <Table
                tableHeaderValues={[
                  "Activity Title",
                  "Date & Time",
                  "Updated By",
                ]}
                tableDataValues={[
                  {
                    type: "text",
                    value: historyData.map((item) => item.activity),
                  },
                  {
                    type: "text",
                    value: historyData.map((item) => item.date),
                  },
                  {
                    type: "text",
                    value: historyData.map((item) => getDetailedChangeInfo(item, true)),
                  },
                ]}
                tableData={historyData}
                hidePagination={true}
                fullWidth={true}
                heading="No history records found"
                subHeading="There are no activity records to display"
                gridStyle={styles.timesheetTableGrid}
              />
            ) : (
              <div className={styles.timelineContainer}>
                <div className={styles.timelineWrapper}>
                  <div className={styles.timelineLineContainer}>
                    <ArrowUpwardIcon
                      style={{
                        color: "#14B15A",
                        width: "50px",
                        height: "50px",
                        marginBottom: "4px",
                      }}
                    />
                    <div className={styles.timelineLine}></div>
                  </div>

                  <div className={styles.timelineItems}>
                    {historyData.map((item, index) => {
                      if (item.status === "highlighted") {
                        return (
                          <div key={index} className={styles.timelineItemWrapper}>
                            <div
                              className={`${styles.timelineItem} ${styles.highlighted}`}
                            >
                              <div className={styles.timelineHighlightBox}>
                                <h3 className={styles.highlightTitle}>
                                  {item.activity}
                                </h3>
                                <p className={styles.highlightDate}>
                                  {item.date}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={index} className={styles.timelineItemWrapper}>
                          <div className={styles.timelineItem}>
                            <div className={styles.timelineContentLeft}>

                              <p className={styles.timelineData1}>{getLeftTitle(item.activity, index)}</p>
                              <p className={styles.timelineDate}>{item.date}</p>
                            </div>
                            <div className={styles.timelineCircle}>
                              <CheckIcon className={styles.checkmark} />
                            </div>
                            <div className={styles.timelineContentRight}>
                              <p className={styles.timelineTitle}>
                                {getRightTitle(item.activity, index)}
                              </p>
                              <p className={styles.timelineDescription}>
                                {getDetailedChangeInfo(item, false)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default HistoryLog;
